import { App, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import type { AssociatedUnitDto, OrgUnitSearchDto } from '@pams/shared';
import { ORG_UNIT_SEARCH_MIN_CHARS } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { withChosenOptions } from '@/lib/selectOptions';
import { labelEntriesOf, useLabelCache } from '@/hooks/useLabelCache';

export type AssociatedUnitsPickerProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  /** Resolved rows from the order itself — the fallback labels for chips not in a result. */
  chosen?: readonly AssociatedUnitDto[];
  disabled?: boolean;
  /** §4d — a removal only warns once the order is live. */
  confirmRemoval?: boolean;
};

const labelFor = (unit: { name: string; shortCode: string }, division: { name: string } | null): string =>
  division ? `${unit.name} (${unit.shortCode}) · ${division.name}` : `${unit.name} (${unit.shortCode})`;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §4] ASSOCIATED UNITS — WHO ELSE MAY SEE THIS ORDER.
 *
 *  ⚠️ **A SEARCH SELECT, NOT `OrgUnitPicker`'s TREE, AND THAT IS FORCED RATHER THAN
 *  PREFERRED.** `GET /org-units/tree` is scoped to the caller's own subtree, and §4c requires
 *  a unit from ANY division — so a control built on that endpoint structurally cannot return
 *  the answer. `PlaceAutocomplete` made the same move for the same reason.
 *
 *  ⚠️ **`withChosenOptions` IS MANDATORY HERE, NOT DEFENSIVE.** The option list is a SEARCH
 *  RESULT, so a chosen chip is almost never in it — and rc-select ends `label ?? item.value`,
 *  which paints a raw ObjectId as a chip (divergence #84). The fallback labels come from the
 *  order's own `associatedOrgUnits`, so the page never has to ask twice.
 *
 *  ⚠️ **THE LABEL NAMES THE DIVISION.** §1b: a cross-division grant the operator cannot SEE
 *  they made is not one they made. `Alipore PS (ALIP) · South Division` is the whole point of
 *  the division being on the wire.
 *
 *  ⚠️ **AND REMOVAL CONFIRMS ONLY ON A PUBLISHED ORDER.** §4d says removing an association
 *  removes sight and the UI must say so — but on a DRAFT nobody has seen it yet, and a dialog
 *  on every chip during authoring is the dialog people learn to dismiss. The sentence is only
 *  TRUE once somebody could have been reading the order.
 *
 *  ⚠️ `modal` FROM `App.useApp()`, NEVER THE STATIC `Modal.confirm` — §8: the static escapes
 *  `ThemeScope` and does not render at all under jsdom, which is why no confirmation in this
 *  repository had a test before P2.
 *
 *  ⚠️ **[amended C2a §4f] RESTORED.** `fc1a786` deleted this header wholesale while keeping
 *  every line of the code it explains — including the `withChosenOptions` call whose whole
 *  point is the second paragraph. That is the most expensive kind of tidying: the next reader
 *  sees a fallback that looks defensive and deletes it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const AssociatedUnitsPicker = ({
  value,
  onChange,
  chosen = [],
  disabled,
  confirmRemoval = false,
}: AssociatedUnitsPickerProps) => {
  const { modal } = App.useApp();
  const [term, setTerm] = useState('');
  /* The debounce lives in the query key, not in a timer: TanStack already de-duplicates. */
  const [debounced, setDebounced] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const onSearch = (next: string): void => {
    setTerm(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebounced(next.trim());
    }, 250);
  };

  const enabled = debounced.length >= ORG_UNIT_SEARCH_MIN_CHARS;
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.orgUnits.search(debounced),
    queryFn: () => api.get<OrgUnitSearchDto>('/org-units/search', { query: { q: debounced } }),
    enabled,
    staleTime: 30_000,
  });

  /* ⚠️ [amended C2a §4c] The label is captured WHEN THE CHIP IS PICKED — see the hook's header
     for why neither a render-time ref nor an effect-merged state survived a lint rule. */
  const { labels: labelCache, remember } = useLabelCache();

  const options = useMemo(() => {
    const rows = (data?.items ?? []).map((row) => ({
      value: row.unit.id,
      label: labelFor(row.unit, row.division),
    }));

    /**
     * Resolution order: cache first (in-session picks that `chosen` does not yet know about),
     * then `chosen` on top (server-saved, always wins). Spreading into Map gives
     * last-writer-wins, so a server label overwrites any stale cached string for the same id.
     */
    const byId = new Map<string, string>([
      ...labelCache,
      ...chosen.map((row): [string, string] => [row.unit.id, labelFor(row.unit, row.division)]),
    ]);

    return withChosenOptions(rows, value ?? [], (id) => {
      const label = byId.get(id);
      return label ? { name: label } : undefined;
    });
  }, [data, chosen, value, labelCache]);

  /**
   * ⚠️ AntD FIRES `onChange` **AND** `onDeselect` FOR ONE REMOVAL, SO THE CONFIRM HAS TO BE
   * ABLE TO REFUSE THE CHANGE — AND THE FIRST DRAFT COULD NOT.
   *
   * With both wired naively, `onChange` applies the removal, the chip disappears, and the
   * dialog then asks whether to do a thing that has already happened. `onChange` therefore
   * SWALLOWS a removal while `confirmRemoval` is on and leaves it to `onDeselect`, which is
   * the only handler that knows WHICH chip went; an addition still goes straight through,
   * because nothing about adding a unit needs a warning.
   */
  const change = (next: string[], option: unknown): void => {
    /* Before the guard below, so a refused REMOVAL still records what is still selected. */
    remember(labelEntriesOf(option));
    /*
     * ⚠️ [amended C2a §4c] THE SEARCH TEXT CLEARS ON A SELECTION, AND IT DID NOT.
     *
     * `searchValue` is CONTROLLED here (`searchValue={term}`), so rc-select's own clear-on-
     * select never ran and the box kept the term that found the chip. Typing the next search
     * APPENDED to it — `ali` then `bar` queried `alibar`, which matches nothing — so after one
     * pick the control looked broken. `AssociatedUnitsPicker.test.tsx`'s rotation case is what
     * names it: it asserted `q: 'bar'` and measured `q: 'alibar'`, and never reached the
     * label-cache claim it was written for.
     */
    setTerm('');
    setDebounced('');
    if (confirmRemoval && next.length < (value ?? []).length) return;
    onChange?.(next);
  };

  const remove = (removed: { value?: unknown }): void => {
    const id = typeof removed.value === 'string' ? removed.value : undefined;
    if (!id) return;
    const next = (value ?? []).filter((row) => row !== id);
    if (!confirmRemoval) {
      onChange?.(next);
      return;
    }

    const name = chosen.find((row) => row.unit.id === id)?.unit.name ?? 'That unit';
    modal.confirm({
      title: `Remove ${name}?`,
      /* ⚠️ THE CONSEQUENCE, IN WORDS, BEFORE IT HAPPENS — §4d. The unit cannot undo it. */
      content: `${name} will no longer be able to see this order.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => {
        onChange?.(next);
      },
    });
  };

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={change}
      onDeselect={(removed) => {
        remove({ value: removed });
      }}
      disabled={disabled}
      /* The server decides what matches; a client filter over a search result filters twice. */
      filterOption={false}
      searchValue={term}
      onSearch={onSearch}
      loading={isFetching}
      options={options}
      placeholder="Search by name or code"
      notFoundContent={enabled ? (isFetching ? 'Searching…' : 'No units match') : 'Type at least three characters'}
      data-testid="associated-units"
      className="w-full"
    />
  );
};
